import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  PpmpDetail,
  PpmpLineItemUpdateRequest,
  PpmpLineItemsRequest,
  PpmpListItem,
  PpmpPpaCandidate,
  PpmpRequest,
} from './ppmpTypes';

/**
 * A PPMP list is ALWAYS scoped to one annual procurement plan.
 *
 * A flat list of every PPMP in the product answers nobody's question — a procurement plan is
 * read inside the annual plan it was filed under — and the server refuses an unscoped query
 * rather than paging the whole table.
 */
export interface PpmpListQuery extends ListQuery {
  annualProcurementPlanId: string;
  /** One department, where the caller is drilling into a roster row. */
  orgUnitId?: string;
  ppa?: string;
}

function toQueryString(query: PpmpListQuery): string {
  const params = new URLSearchParams();

  params.set('annualProcurementPlanId', query.annualProcurementPlanId);
  if (query.orgUnitId) params.set('orgUnitId', query.orgUnitId);
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  // Present-key, not truthiness — an empty value is "none of these", which matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.ppa !== undefined) params.set('ppa', query.ppa);

  return `?${params.toString()}`;
}

export const ppmpsApi = {
  list: (query: PpmpListQuery, signal?: AbortSignal) =>
    api<PagedResult<PpmpListItem>>(`/api/ppmps${toQueryString(query)}`, { signal }),

  /**
   * One PPMP and its lines.
   *
   * `includeDeleted` is what makes the line RESTORE reachable at all — without it nothing on
   * any screen could name a deleted line to put back. The plan's own total never moves with
   * it: it sums the LIVE lines.
   */
  get: (id: string, includeDeleted = false, signal?: AbortSignal) =>
    api<PpmpDetail>(`/api/ppmps/${id}${includeDeleted ? '?includeDeleted=true' : ''}`, { signal }),

  /**
   * The PPAs one department may file against, with their budgeted lines.
   *
   * Carries `ppmps.create` server-side rather than `read`: it discloses what a department has
   * been budgeted for, which is a wider fact than the list of plans it has filed.
   */
  ppaCandidates: (annualProcurementPlanId: string, orgUnitId: string, signal?: AbortSignal) =>
    api<PpmpPpaCandidate[]>(
      `/api/ppmps/ppa-candidates?annualProcurementPlanId=${annualProcurementPlanId}&orgUnitId=${orgUnitId}`,
      { signal },
    ),

  create: (request: PpmpRequest) => api<PpmpDetail>('/api/ppmps', { method: 'POST', body: request }),

  update: (id: string, request: PpmpRequest) =>
    api<PpmpDetail>(`/api/ppmps/${id}`, { method: 'PUT', body: request }),

  /** Soft delete. Its LINES are left alone, which is what makes the restore faithful. */
  deactivate: (id: string) => api<void>(`/api/ppmps/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/ppmps/${id}/restore`, { method: 'POST' }),

  /** APPENDS a batch of lines. All four line calls carry `ppmps.update`. */
  addLineItems: (id: string, request: PpmpLineItemsRequest) =>
    api<PpmpDetail>(`/api/ppmps/${id}/line-items`, { method: 'POST', body: request }),

  updateLineItem: (id: string, lineItemId: string, request: PpmpLineItemUpdateRequest) =>
    api<PpmpDetail>(`/api/ppmps/${id}/line-items/${lineItemId}`, { method: 'PUT', body: request }),

  deleteLineItem: (id: string, lineItemId: string) =>
    api<PpmpDetail>(`/api/ppmps/${id}/line-items/${lineItemId}`, { method: 'DELETE' }),

  restoreLineItem: (id: string, lineItemId: string) =>
    api<PpmpDetail>(`/api/ppmps/${id}/line-items/${lineItemId}/restore`, { method: 'POST' }),
};
